import { useCallback, useEffect, useRef, useState } from 'react';
import type { CustomFieldSchema, CustomFieldValue } from '@/types/custom-fields';
import {
  deriveCustomFieldDraftState,
  getInitialCustomFieldDraftValue,
} from '@/components/organisms/custom-fields/custom-field-form-utils';

interface UseEstimateCustomFieldStateArgs {
  isEditMode: boolean;
  estimateCustomFields: CustomFieldSchema[];
  existingCfValues?: CustomFieldValue[] | null;
}

export function useEstimateCustomFieldState({
  isEditMode,
  estimateCustomFields,
  existingCfValues,
}: UseEstimateCustomFieldStateArgs) {
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [activeFieldKeys, setActiveFieldKeys] = useState<string[]>([]);

  const didPopulateCfRef = useRef(false);

  useEffect(() => {
    if (!isEditMode || !existingCfValues || !estimateCustomFields.length) return;
    if (didPopulateCfRef.current) return;

    didPopulateCfRef.current = true;
    const next = deriveCustomFieldDraftState(existingCfValues, estimateCustomFields, 'edit');
    setCustomFields(next.customFields);
    setActiveFieldKeys(next.activeFieldKeys);
  }, [isEditMode, existingCfValues, estimateCustomFields]);

  useEffect(() => {
    if (isEditMode || !estimateCustomFields.length) return;

    const next = deriveCustomFieldDraftState(null, estimateCustomFields, 'create');
    if (next.activeFieldKeys.length === 0) return;

    setActiveFieldKeys((prev) => {
      const missing = next.activeFieldKeys.filter((key) => !prev.includes(key));
      return missing.length ? [...prev, ...missing] : prev;
    });
    setCustomFields((prev) => ({ ...next.customFields, ...prev }));
  }, [estimateCustomFields, isEditMode]);

  // Keep a ref of the live draft so applyInheritedDraftValues can decide per-field
  // without re-running on every keystroke.
  const customFieldsRef = useRef(customFields);
  customFieldsRef.current = customFields;
  // Last inherited values we applied, per field, so a source change updates a field
  // the user hasn't overridden, but a manual override is never clobbered.
  const lastInheritedRef = useRef<Record<string, string>>({});

  // COPY_ON_CREATE pre-fill: merge inherited draft values, only into fields the user
  // hasn't touched (unset, empty, or still equal to what we last inherited).
  const applyInheritedDraftValues = useCallback(
    (inherited: Record<string, string>) => {
      if (isEditMode) return;
      const current = customFieldsRef.current;
      const last = lastInheritedRef.current;
      lastInheritedRef.current = inherited;
      const updates: Record<string, string> = {};
      const newKeys: string[] = [];
      for (const [key, value] of Object.entries(inherited)) {
        const cur = current[key];
        if (cur === undefined || cur === '' || cur === last[key]) {
          if (cur !== value) updates[key] = value;
          if (cur === undefined) newKeys.push(key);
        }
      }
      // A previously inherited field whose new source no longer provides a value:
      // clear it if the user hasn't overridden it (draft still equals last inherited),
      // so switching contact/address never leaves the prior source's value behind.
      for (const key of Object.keys(last)) {
        if (key in inherited) continue;
        if (current[key] === last[key] && current[key] !== '') updates[key] = '';
      }
      if (Object.keys(updates).length > 0) {
        setCustomFields((prev) => ({ ...prev, ...updates }));
      }
      if (newKeys.length > 0) {
        setActiveFieldKeys((prev) => {
          const missing = newKeys.filter((key) => !prev.includes(key));
          return missing.length ? [...prev, ...missing] : prev;
        });
      }
    },
    [isEditMode],
  );

  const setCustomField = (key: string, value: string) =>
    setCustomFields((prev) => ({ ...prev, [key]: value }));

  const setActiveKeysWithDefaults = (keys: string[]) => {
    setActiveFieldKeys(keys);
    setCustomFields((prev) => {
      const next = { ...prev };
      for (const key of keys) {
        if (next[key] !== undefined) continue;
        const schema = estimateCustomFields.find((item) => item.definition.fieldKey === key);
        if (schema) next[key] = getInitialCustomFieldDraftValue(schema);
      }
      return next;
    });
  };

  const removeField = (fieldKey: string) => {
    setActiveFieldKeys((prev) => prev.filter((key) => key !== fieldKey));
    setCustomFields((prev) => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  return {
    customFields,
    activeFieldKeys,
    setCustomField,
    setActiveFieldKeys: setActiveKeysWithDefaults,
    removeField,
    applyInheritedDraftValues,
  };
}
